import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, Lock, Shield, BarChart3, Share2 } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-green-50 to-teal-100">
      <div className="max-w-4xl mx-auto">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 text-lg font-semibold hover:scale-105 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <Card className="p-8 md:p-12 bg-white shadow-xl" style={{ borderRadius: '24px' }}>
          <div className="flex items-center gap-4 mb-8">
            <div className="p-4 bg-green-100 rounded-2xl">
              <Lock className="w-10 h-10 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Privacy Policy</h1>
              <p className="text-gray-500">Little Genius Hub & Affiliates</p>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-8">Last Updated: 1/1/26</p>

          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-8">
              Little Genius Hub ("we," "our," or "us") is committed to protecting the privacy of children and families who use our services. This Privacy Policy explains how we collect, use, and protect personal information in compliance with the Children's Online Privacy Protection Act (COPPA) and other applicable laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-green-500" />
              1. Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Information Collected from Parents</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Payment information (processed securely through third-party providers)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Information Collected from Children</h3>
            <p className="mb-4">We collect only what is necessary for the educational experience:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Username or nickname</li>
              <li>Learning progress and activity data</li>
              <li>App preferences (age group, learning level)</li>
            </ul>

            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-r-lg">
              <p className="font-semibold text-green-800 mb-2">We do not collect:</p>
              <ul className="list-disc pl-6 space-y-1 text-green-800">
                <li>Physical addresses</li>
                <li>Phone numbers</li>
                <li>Precise location data</li>
                <li>Social media information</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">2. How We Use Information</h2>
            <p className="mb-4">We use information only to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Operate and improve the Service</li>
              <li>Personalize learning experiences</li>
              <li>Track educational progress</li>
              <li>Provide customer support</li>
              <li>Maintain safety and security</li>
            </ul>
            <p className="mb-6 font-bold text-green-700 text-lg">We never sell children's personal information.</p>

            {/* Kids Advertising Statement */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">3. Children's Advertising Policy</h2>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-r-lg">
              <p className="font-semibold text-green-800 mb-3">No Advertising to Children</p>
              <p className="text-green-800 mb-2">The Service does not display third-party advertisements or use behavioral tracking.</p>
              <ul className="list-disc pl-6 space-y-1 text-green-800">
                <li><strong>No behavioral advertising</strong> – We do not track children's behavior to serve targeted ads</li>
                <li><strong>No interest-based ads</strong> – We do not use children's interests or activities for advertising purposes</li>
                <li><strong>No third-party ad networks</strong> – We do not integrate any third-party advertising networks in our Service</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">4. Parental Rights</h2>
            <p className="mb-4">Parents have the right to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Review their child's personal information</li>
              <li>Request deletion of data</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="mb-6">
              Requests can be made by contacting: <a href="mailto:support@littlegeniushub.com" className="text-blue-600 hover:underline">support@littlegeniushub.com</a>
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">5. Data Security</h2>
            <p className="mb-6">
              We use reasonable administrative, technical, and physical safeguards to protect personal information. However, no system is 100% secure.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">6. Third-Party Services</h2>
            <p className="mb-6">
              We may use trusted third-party providers (e.g., payment processors) who comply with applicable privacy laws and COPPA standards.
            </p>

            {/* Platform Analytics Disclosure */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-green-500" />
              7. Platform Analytics Disclosure
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
              <p className="text-blue-800 mb-3">To improve app performance and stability, we may use:</p>
              <ul className="list-disc pl-6 mb-3 space-y-1 text-blue-800">
                <li><strong>Apple Analytics</strong> – Basic app performance and crash reporting on iOS devices</li>
                <li><strong>Google Firebase Analytics</strong> – Basic app performance and crash reporting on Android devices</li>
              </ul>
              <p className="text-blue-800 font-semibold">Important: All analytics data is aggregated and non-personal. We do not use analytics to identify individual children or track personal behavior.</p>
            </div>

            {/* Data Sharing Clarification */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <Share2 className="w-6 h-6 text-green-500" />
              8. Data Sharing Clarification
            </h2>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6 rounded-r-lg">
              <p className="font-semibold text-green-800 mb-3">Our Data Sharing Commitments:</p>
              <ul className="list-disc pl-6 space-y-2 text-green-800">
                <li><strong>Service Providers Only:</strong> Data is shared only with essential service providers (payment processors, hosting) who are contractually bound to protect it</li>
                <li><strong>Never Sold:</strong> We never sell personal information of any user, especially children</li>
                <li><strong>No Marketing to Children:</strong> We never share children's data for marketing purposes</li>
                <li><strong>No Third-Party Marketing:</strong> We do not share data with third parties for their marketing purposes</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">9. Data Retention</h2>
            <p className="mb-6">
              We retain personal data only as long as necessary to provide services or meet legal requirements.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">10. Changes to This Policy</h2>
            <p className="mb-6">
              We may update this Privacy Policy periodically. Continued use of the Service indicates acceptance of updates.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">11. Contact Us</h2>
            <p className="mb-4">For privacy questions or requests:</p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p><strong>Email:</strong> <a href="mailto:support@littlegeniushub.com" className="text-blue-600 hover:underline">support@littlegeniushub.com</a></p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
